import { RequestStatus } from '@oncolens/oncolens-ui/lib/dto/event/request';
import { createStore, FormStoreProps } from '@oncolens/oncolens-ui/lib/libraries/FormStore';
import { PanelDropdownItemDto } from '@oncolens/oncolens-ui/lib/components/BiomarkerResults/BiomarkerResults.api';
import { FullCollaborationRequestDto } from '@/features/requests/dto/FullCollaborationRequest';
import schema from './schema';
import { nanoid } from 'nanoid';
import { getLastBiopsyEvent, getPrimaryDisease, getTargetEpisode, getLastPathologyEvent } from '@/utils/templateUtils';
import { TreatmentHistoryDto } from './dtos/TreatmentHistory';
import { ComorbiditiesNames } from './OsuStore.constants';
import { mapDiseases } from './OsuStore.helpers';

export interface FormValues {
  id: string | null;
  patient: any;
  referringProvider: any;
  patientHistory: any;
  diseases: any;
  noTreatmentHistory: boolean;
  treatmentHistory: TreatmentHistoryDto[];
  panels: PanelDropdownItemDto[];
  submitTo: any;
  templateId: string | null;
  workflowId: string | null;
  organizationId: string | null;
  submissionId: string | null;
  isPending: boolean;
  status: RequestStatus | null;
  hasGivenConsent: boolean | undefined;
  assignedPhysicianId: string | undefined;
}

const store = createStore<FormValues>(
  {
    id: null,
    referringProvider: {},
    patient: {},
    patientHistory: {},
    diseases: {
      [nanoid()]: {
        isSaved: false,
        isPrimary: true,
        cancerTypeId: null,
        cancerLocationId: null,
        cancerPathologyId: null,
        cancerPathologyOtherComments: null,
        initialDiagnosisDate: null,
        episodes: {
          [nanoid()]: {
            isTargetEpisode: true,
            diagnosisStatus: null,
            episodeDate: null,
            behaviorId: null,
            settingId: null,
            laterality: null,
            recurrenceExtentId: null,
            areasOfRecurrence: [],
            areasOfMetastasis: [],
            areasOfMetastasisOtherComments: null,
            areasOfRecurrenceOtherComments: null,
            riskFactors: [],
            staging: {},
            isSaved: false,
          },
        },
      },
    },
    noTreatmentHistory: false,
    treatmentHistory: [],
    panels: [],
    submitTo: {
      highPriority: false,
      reasonsForRequest: '',
    },
    templateId: null,
    organizationId: null,
    workflowId: null,
    submissionId: null,
    isPending: true,
    status: null,
    hasGivenConsent: false,
    assignedPhysicianId: undefined,
  },
  schema
);

export function osuStoreHydrate(
  state: FormStoreProps<FormValues>,
  data: FullCollaborationRequestDto
) {
  const biopsyEvent = getLastBiopsyEvent(data.eventHistories);
  const pathologyEvent = getLastPathologyEvent(data.eventHistories)
  const diseases = data.diseases;
  const primaryDisease = getPrimaryDisease(diseases);
  const targetEpisode = getTargetEpisode(primaryDisease?.episodes);

  state.id = data.request.id;
  state.patient = {
    id: data.patient.id,
    sexId: data.patient.sexId,
    raceId: data.patient.raceId,
    ethnicityId: data.patient.ethnicityId,
    age: data.patient.age,
    zip: data.patient.zip,
    dateOfLastBiopsy: biopsyEvent?.eventDate,
    isTissueAvailable:
      biopsyEvent?.biopsyIsTissueAvailable === true
        ? 1
        : biopsyEvent?.biopsyIsTissueAvailable === false
        ? 0
        : undefined,
    biopsyCollectionSite: biopsyEvent?.biopsyCollectionSite,
    pathologyText: pathologyEvent?.comments,
    pathologyStatus: pathologyEvent?.status,
    pathologyDate: pathologyEvent?.eventDate,
  };
  state.assignedPhysicianId = data.request.assignedPhysicianId;

  state.submitTo = {
    reasonsForRequest: data.request.topicForDiscussion,
    highPriority: data.request.isHighPriority,
    preferredPhysicianId: data.request.preferredPhysicianId,
    autoImmuneDisease: !!targetEpisode?.riskFactors?.find(
      (riskFactor) => riskFactor.description === ComorbiditiesNames.AutoImmuneDisease
    ),
    uncontrolledDiabetes: !!targetEpisode?.riskFactors?.find(
      (riskFactor) => riskFactor.description === ComorbiditiesNames.UncontrolledDiabetes
    ),
    activeInfection: !!targetEpisode?.riskFactors?.find(
      (riskFactor) => riskFactor.description === ComorbiditiesNames.ActiveInfection
    ),
    dvtpe: !!targetEpisode?.riskFactors?.find(
      (riskFactor) => riskFactor.description === ComorbiditiesNames.DVTPE
    ),
    lowEjectionFraction: !!targetEpisode?.riskFactors?.find(
      (riskFactor) => riskFactor.description === ComorbiditiesNames.LowEjectionFraction
    ),
    hepatitis: !!targetEpisode?.riskFactors?.find(
      (riskFactor) => riskFactor.description === ComorbiditiesNames.Hepatitis
    ),
    hiv: !!targetEpisode?.riskFactors?.find(
      (riskFactor) => riskFactor.description === ComorbiditiesNames.HIV
    ),
    highQt: !!targetEpisode?.riskFactors?.find(
      (riskFactor) => riskFactor.description === ComorbiditiesNames.HighQT
    ),
    brainMetastasis: !!targetEpisode?.riskFactors?.find(
      (riskFactor) => riskFactor.description === ComorbiditiesNames.BrainMetastasis
    ),
  };

  state.submissionId = data.request.displayInternalId;
  state.workflowId = data.request.workflowId;
  state.organizationId = data.request.organizationId;
  state.status = data.request.status || null;

  state.referringProvider = {
    email: data.request.referringProviderEmail,
    name: data.request.referringProviderName,
    phone: data.request.referringProviderPhone,
  };

  if (diseases) {
    state.diseases = mapDiseases(diseases);
    if (state.patientHistory) {
      state.patientHistory.chiefComplaint = primaryDisease?.chiefComplaint;
      state.patientHistory.medicalHistory = primaryDisease?.medicalHistory;
      state.patientHistory.familyHistory = primaryDisease?.familyHistory;
      state.patientHistory.socialHistory = primaryDisease?.socialHistory;
      state.patientHistory.miscellaneousCaseDetails = primaryDisease?.miscellaneousCaseDetails;
      state.patientHistory.menopausalStatus = primaryDisease?.menopausalStatus;
      state.patientHistory.breastOvarianCancerNear =
        primaryDisease?.breastOvarianCancerNear === true
          ? 1
          : primaryDisease?.breastOvarianCancerNear === false
          ? 0
          : undefined;
    }
  }

  state.treatmentHistory = data.treatmentHistories || [];
  state.panels = data.panels || [];
}

export function formDataToRequestData(formData: FormValues): FullCollaborationRequestDto {
  return {
    ...formData.patient,
    workflowId: formData.workflowId,
    organizationId: formData.organizationId,
    diseases: formData.diseases,
    // @TODO add other props
  };
}

export { store as default };
